import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faCrown
} from '@fortawesome/free-solid-svg-icons';
import { paymentAPI, subscriptionAPI } from '../services/api';
import '../assets/css/payment-verification.css';

const PaymentVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [countdown, setCountdown] = useState(5);

  // Check if user is already premium and redirect them
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isPremium = user.isPremium || user.premium_status === 'active';

    // Only redirect if we don't have a payment reference (meaning they're not in the middle of payment)
    const reference = searchParams.get('reference') || searchParams.get('paymentReference') ||
                     searchParams.get('trxref') ||
                     localStorage.getItem('pending_payment_reference');

    if (isPremium && !reference) {
      const userType = user.user_type || user.userType;
      if (userType === 'seller') {
        navigate('/premium-seller-dashboard', { replace: true });
      } else if (userType === 'buyer') {
        navigate('/premium-buyer-dashboard', { replace: true });
      } else {
        navigate('/homepage', { replace: true });
      }
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    verifyPayment();
  }, []);

  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      redirectToDashboard();
    }
  }, [status, countdown]);

  const verifyPayment = async () => {
    try {
      // Get payment reference from URL
      const reference = searchParams.get('reference') || searchParams.get('paymentReference') ||
                       searchParams.get('trxref') ||
                       localStorage.getItem('pending_payment_reference');

      if (!reference) {
        setStatus('error');
        setMessage('Payment reference not found. Please try again.');
        return;
      }

      // Verify payment with backend - new backend automatically processes premium subscription
      const verificationResponse = await paymentAPI.verifyPayment(reference);

      if (verificationResponse && verificationResponse.success) {
        const paymentData = verificationResponse.data;

        // Check if payment is for premium membership
        if (paymentData.payment_type === 'premium_membership') {
          setStatus('success');
          setMessage('Payment verified! Your premium subscription is now active.');

          // Update user info in localStorage
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.isPremium = true;
          user.is_premium = true;
          user.premium_status = 'active';
          user.subscriptionStatus = 'active';
          user.subscription_status = 'active';
          localStorage.setItem('user', JSON.stringify(user));

          // Trigger AuthContext to refresh user data
          window.dispatchEvent(new Event('userUpdated'));

          // Clear pending data
          localStorage.removeItem('pending_subscription_plan');
          localStorage.removeItem('pending_payment_reference');
          localStorage.removeItem('pending_payment_id');
        } else {
          setStatus('success');
          setMessage('Payment verified successfully!');
        }
      } else {
        setStatus('error');
        setMessage(verificationResponse?.message || 'Payment verification failed. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'An error occurred while verifying your payment. Please contact support.');
    }
  };

  const redirectToDashboard = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Check multiple sources for user type (in order of priority)
    const userType = user.user_type ||
                     user.userType ||
                     localStorage.getItem('pending_user_type') ||
                     localStorage.getItem('userType');


    // Clear pending user type
    localStorage.removeItem('pending_user_type');

    if (userType === 'seller') {
      navigate('/premium-seller-dashboard');
    } else if (userType === 'buyer') {
      navigate('/premium-buyer-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleManualRedirect = () => {
    redirectToDashboard();
  };

  return (
    <div className="AutoboyExpress-verification-container">
      <div className="AutoboyExpress-verification-card">
        {status === 'verifying' && (
          <>
            <div className="AutoboyExpress-verification-icon verifying">
              <FontAwesomeIcon icon={faSpinner} spin size="4x" />
            </div>
            <h2>Verifying Payment</h2>
            <p>{message}</p>
            <div className="AutoboyExpress-verification-loader">
              <div className="loader-bar"></div>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="AutoboyExpress-verification-icon success">
              <FontAwesomeIcon icon={faCheckCircle} size="4x" />
            </div>
            <h2>Payment Successful!</h2>
            <p>{message}</p>
            <div className="AutoboyExpress-verification-premium-badge">
              <FontAwesomeIcon icon={faCrown} />
              <span>Welcome to Premium</span>
            </div>
            <p className="AutoboyExpress-verification-redirect">
              Redirecting to your premium dashboard in {countdown} seconds...
            </p>
            <button
              className="AutoboyExpress-verification-btn"
              onClick={handleManualRedirect}
            >
              Go to Dashboard Now
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="AutoboyExpress-verification-icon error">
              <FontAwesomeIcon icon={faTimesCircle} size="4x" />
            </div>
            <h2>Payment Failed</h2>
            <p>{message}</p>
            <div className="AutoboyExpress-verification-actions">
              <button
                className="AutoboyExpress-verification-btn secondary"
                onClick={() => navigate(-1)}
              >
                Go Back
              </button>
              <button
                className="AutoboyExpress-verification-btn"
                onClick={() => window.location.href = 'mailto:support@AutoboyExpress.com'}
              >
                Contact Support
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentVerification;
